import { Eye, Edit, Trash2, Navigation, Truck, AlertTriangle, Share2, Users } from 'lucide-react';
import { getStatusConfig, formatCurrency } from '../../../../utils/utils';
import PaymentTypeIcon from '@/pages/admin/components/icons/PaymentTypeIcon';

function isToday(dateString: string) {
  const today = new Date();
  const date = new Date(dateString);
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

interface TransportTableProps {
  transports: any[];
  onView: (transport: any) => void;
  onEdit: (transport: any) => void;
  onDelete: (transport: any) => void;
  onShareLocation: (transport: any) => void;
  onTransfer?: (transport: any) => void; // Funcție nouă pentru transfer
}

const TransportTable = ({ 
  transports, 
  onView, 
  onEdit, 
  onDelete, 
  onShareLocation,
  onTransfer 
}: TransportTableProps) => {
  return (
    <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left text-gray-600 text-sm font-medium">
              <th className="p-4">Transport</th>
              <th className="p-4">Client & Rută</th>
              <th className="p-4">Status</th>
              <th className="p-4">Data & Plata</th>
              <th className="p-4">Șofer & Vehicul</th>
              <th className="p-4">Poziția Curentă</th>
              <th className="p-4">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {transports.map((transport) => {
              const statusConfig = getStatusConfig(transport.status);
              return (
                <tr key={transport.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  {/* Transport ID */}
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                        <Truck className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-mono text-sm font-medium">{transport.id}</span>
                    </div>
                  </td>

                  {/* Client & Route */}
                  <td className="p-4">
                    <div>
                      <p className="font-medium text-gray-900">{transport.client}</p>
                      <p className="text-sm text-gray-500">{transport.route}</p>
                      {transport.description && (
                        <p className="text-xs text-gray-400 truncate max-w-xs">{transport.description}</p>
                      )}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="p-4">
                    <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${statusConfig.color} flex items-center w-fit`}>
                      {statusConfig.icon}
                      <span className="ml-1.5">{statusConfig.text}</span>
                    </span>
                  </td>

                  {/* Date & Value */}
                  <td className="p-4">
                    <div>
                      <p className="text-sm text-gray-600">{transport.date}</p>
                      <div className="flex items-center space-x-1">
                        <PaymentTypeIcon tip_plata={transport.tip_plata} />
                        
                        {/* Afișează suma doar dacă: 
                            - tip_plata este cash
                            - și data este azi */}
                        {transport.tip_plata === 'cash' && isToday(transport.date) && (
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(transport.value)}
                          </span>
                        )}
                      </div>

                      {transport.prioritate === 'urgenta' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Urgent
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Driver & Vehicle */}
                  <td className="p-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{transport.driver}</p>
                        {transport.was_transferred && (
                          <div className="flex flex-col gap-1">
                            <span 
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200 shadow-sm animate-pulse cursor-help"
                              title={transport.transfer_info ? 
                                `Transferat de la ${transport.transfer_info.fromDriver}\n` +
                                `Motiv: ${transport.transfer_info.reason}\n` +
                                `Data: ${transport.transfer_info.lastDate}` : 
                                'Transport transferat'}
                            >
                              <Users className="w-3 h-3 mr-1" />
                              Transferat
                            </span>
                            {transport.transfer_count > 1 && (
                              <span className="text-xs text-indigo-600 font-medium">
                                {transport.transfer_count}× transferuri
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{transport.vehicle}</p>
                      {transport.was_transferred && transport.last_transfer_reason && (
                        <p className="text-xs text-indigo-600 mt-0.5 italic">
                          ↳ {transport.last_transfer_reason}
                        </p>
                      )}
                    </div>
                  </td>

                  {/* Current Position */}
                  <td className="p-4">
                    <div className="max-w-xs">
                      {transport.vehicul_adresa ? (
                        <div>
                          <p className="text-xs text-gray-600 truncate" title={transport.vehicul_adresa}>
                            📍 {transport.vehicul_adresa}
                          </p>
                          {transport.vehicul_ultima_pozitie && (
                            <p className="text-xs text-gray-400">
                              {new Date(transport.vehicul_ultima_pozitie).toLocaleString('ro-RO')}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">Poziție necunoscută</p>
                      )}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="p-4">
                    <div className="flex items-center space-x-1">
                      {/* View Details */}
                      <button 
                        onClick={() => onView(transport)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Vezi detalii"
                      >
                        <Eye className="w-4 h-4" />
                      </button>

                      {/* Edit */}
                      <button 
                        onClick={() => onEdit(transport)}
                        className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        title="Editează"
                      >
                        <Edit className="w-4 h-4" />
                      </button>

                      {/* Transfer - doar pentru statusuri relevante */}
                      {onTransfer && ['pe_drum', 'in_asteptare', 'programat'].includes(transport.status) && (
                        <button 
                          onClick={() => onTransfer(transport)}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Transferă către alt șofer"
                        >
                          <Users className="w-4 h-4" />
                        </button>
                      )}

                      {/* Share Location - pentru toate statusurile */}
                      <button 
                        onClick={() => onShareLocation(transport)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Partajează locația"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>

                      {/* Send Location (doar pentru transporturi în tranzit) */}
                      {transport.status === 'pe_drum' && (
                        <button 
                          onClick={() => onShareLocation(transport)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Trimite locația live"
                        >
                          <Navigation className="w-4 h-4" />
                        </button>
                      )}

                      {/* Delete */}
                      <button 
                        onClick={() => onDelete(transport)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Șterge"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State for Table */}
      {transports.length === 0 && (
        <div className="p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Truck className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500">Nu există transporturi de afișat</p>
        </div>
      )}
    </div>
  );
};

export default TransportTable;